import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { setSessionToken, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * AuthCallback — handles the post-Google-OAuth landing at any URL whose
 * hash contains `#session_id=...`. Exchanges the session_id for a Born
 * Day session via the backend, then redirects to /app.
 *
 * Steve (Feb 2026) reported users getting "looped" back to /login after
 * Google sign-in. Root cause: cross-site cookie blocking (bornday.me ↔
 * *.emergent.host). The fix is in two parts:
 *   1) Persist the session_token in localStorage too (lib/api.js now
 *      sends it as a Bearer header on every request) so browsers that
 *      drop the cookie still have a working auth channel.
 *   2) If the exchange or follow-up /auth/me fails, SHOW the error and
 *      give the user a "Try again" button — never silently bounce back
 *      to /login. A silent bounce looks like a loop to non-technical
 *      family members and damages trust in the platform.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);

    (async () => {
      try {
        // Exchange the temp session_id for a real Born Day session.
        const { data } = await api.post("/auth/google/session", { session_id });

        // Belt-and-suspenders: also stash the token in localStorage so
        // 3rd-party-cookie blockers (Safari ITP, Brave, hardened Chrome)
        // can't lock the user out — the api interceptor will pin it on
        // every subsequent request as `Authorization: Bearer <token>`.
        if (data?.session_token) setSessionToken(data.session_token);

        // Strip the session_id from the URL bar before refreshing auth
        // so a back-button press doesn't try to re-exchange a now-stale id.
        window.history.replaceState({}, "", "/app");

        // Pull /api/auth/me to populate the AuthProvider, then navigate.
        await refresh();
        navigate("/app", { replace: true });
      } catch (e) {
        console.error("[auth-callback] Google session exchange failed:", e);
        setError(formatError(e) || "Sign-in failed. Please try again.");
      }
    })();
  }, [location, navigate, refresh]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-4" style={{ background: "var(--bg)", color: "var(--text)" }}>
        <div className="bd-surface p-8 max-w-md w-full flex flex-col items-center gap-4 text-center" data-testid="auth-callback-error">
          <AlertTriangle size={28} style={{ color: "#ff3b30" }} />
          <h2 className="text-xl font-bold">We couldn&apos;t finish signing you in</h2>
          <p className="text-sm" style={{ color: "var(--text-dim)" }}>{error}</p>
          <p className="text-xs" style={{ color: "var(--text-dim)" }}>
            If this keeps happening, try unblocking third-party cookies for{" "}
            <strong>bornday.me</strong> in your browser, or use email + password instead.
          </p>
          <div className="flex gap-2 w-full">
            <button
              type="button"
              onClick={() => { window.location.href = "/login"; }}
              className="bd-btn bd-btn-ghost flex-1"
              data-testid="auth-callback-back-to-login"
            >
              Back to sign-in
            </button>
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/app";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="bd-btn flex-1"
              data-testid="auth-callback-retry"
            >
              Try Google again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="bd-surface p-8 flex flex-col items-center gap-3" data-testid="auth-callback">
        <Loader2 size={28} className="animate-spin" />
        <span style={{ color: "var(--text-dim)" }}>Signing you in…</span>
      </div>
    </div>
  );
}
